// lib/types/notifications.ts
//
// Shared notification types for the player app. The DB-level shape comes
// from types/supabase.ts (Database['public']['Tables']['notifications']
// after Task 13 regen). This file adds the discriminated union helpers
// and human-friendly labels the UI consumes.

import type { Database } from '@/types/supabase'

export const NOTIFICATION_TYPES = [
  'gameweek_start',
  'gameweek_end',
  'transfer_deadline',
  'price_change',
  'achievement_unlocked',
  'prize_won',
  'payment_received',
  'system_alert',
  'admin_broadcast'
] as const
export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

export const NOTIFICATION_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const
export type NotificationPriority = (typeof NOTIFICATION_PRIORITIES)[number]

// DB row, with notification_type widened to include unknown registry codes.
export type NotificationRow = Database['public']['Tables']['notifications']['Row']

// `notification_type` is widened to `NotificationType | string` deliberately:
// the registry (public.notification_types) is a runtime-extensible source of
// truth. New codes can be inserted without a migration, so the UI must not
// blow up when it sees a code that's not in NOTIFICATION_TYPES at build time.
// Use `isKnownType` to narrow before indexing into TYPE_LABEL etc.
export interface Notification extends Omit<
  NotificationRow,
  'metadata' | 'priority' | 'notification_type'
> {
  notification_type: NotificationType | string
  priority: NotificationPriority
  metadata: Record<string, unknown> | null
}

export const TYPE_LABEL: Record<NotificationType, string> = {
  gameweek_start: 'Gameweek',
  gameweek_end: 'Gameweek',
  transfer_deadline: 'Transfers',
  price_change: 'Prices',
  achievement_unlocked: 'Achievement',
  prize_won: 'Prize',
  payment_received: 'Payment',
  system_alert: 'System',
  admin_broadcast: 'Announcement'
}

export const PRIORITY_DOT_COLOR: Record<NotificationPriority, string> = {
  low: 'bg-zinc-400',
  normal: 'bg-blue-500',
  high: 'bg-amber-500',
  urgent: 'bg-red-500'
}

export function isKnownType(t: string): t is NotificationType {
  return (NOTIFICATION_TYPES as readonly string[]).includes(t)
}

export function asNotification(row: NotificationRow): Notification {
  return {
    ...row,
    priority: (row.priority ?? 'normal') as NotificationPriority,
    metadata: (row.metadata as Record<string, unknown> | null) ?? null
  }
}
